import { Tbody, Thead, Tr } from "@biblioteksentralen/react";
import { Period, WEEKDAY_LABELS, getNorwegianWeekday, getPeriodAsDates, getWeekdayIndex } from "@libry-content/common";
import { SanityKeyed, SpecialOpeningHours } from "@libry-content/types";
import { sort } from "radash";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { StyledTable, TableHeaderCell } from "../OpeningHoursTable";
import SpecialOpeningHoursDay, { getVerticalTableCellPadding } from "./SpecialOpeningHoursDay";

const sortByWeekday = (dates: Date[]) => sort(dates, (date) => getWeekdayIndex(getNorwegianWeekday(date)));

type SpecialOpeningHoursPeriodHeaderProps = {
  normalHoursLabel: string;
  selfServiceLabel: string;
  hasSelfService: boolean;
  note?: string;
  level?: number;
};

export const SpecialOpeningHoursPeriodHeader = ({
  normalHoursLabel,
  selfServiceLabel,
  hasSelfService,
  note,
  level = 0,
}: SpecialOpeningHoursPeriodHeaderProps) => {
  if (!hasSelfService) return null;

  const padding = `0.25rem ${getVerticalTableCellPadding(level)}rem !important`;

  return (
    <Thead width="100%">
      <Tr>
        <TableHeaderCell padding={padding} fontSize="sm">
          {note}
        </TableHeaderCell>
        <TableHeaderCell whiteSpace="nowrap" padding={padding}>
          {normalHoursLabel}
        </TableHeaderCell>
        <TableHeaderCell whiteSpace="nowrap" padding={padding}>
          {selfServiceLabel}
        </TableHeaderCell>
      </Tr>
    </Thead>
  );
};

type SpecialOpeningHoursPeriodProps = {
  specialHours: SanityKeyed<SpecialOpeningHours> & Period;
  hasSelfService: boolean;
  selfServiceLabel: string;
  normalHoursLabel: string;
};

const SpecialOpeningHoursPeriod = ({
  specialHours,
  hasSelfService,
  selfServiceLabel,
  normalHoursLabel,
}: SpecialOpeningHoursPeriodProps) => {
  const { lang, ts } = useTranslation();

  return (
    <StyledTable>
      <SpecialOpeningHoursPeriodHeader
        hasSelfService={hasSelfService}
        normalHoursLabel={normalHoursLabel}
        selfServiceLabel={selfServiceLabel}
        note={ts(specialHours.note)}
        level={1}
      />
      <Tbody width="100%">
        {sortByWeekday(getPeriodAsDates(specialHours).slice(0, 7))!.map((date, index) => {
          const weekday = getNorwegianWeekday(date);

          return (
            <SpecialOpeningHoursDay
              key={`${specialHours._key}-${weekday}`}
              label={WEEKDAY_LABELS[lang][weekday]}
              normalHoursDay={specialHours.normalHours?.[weekday]}
              selfServiceHoursDay={specialHours.selfServiceHours?.[weekday]}
              hasSelfService={hasSelfService}
              oddTableItem={index % 2 === 1}
              level={1}
            />
          );
        })}
      </Tbody>
    </StyledTable>
  );
};

export default SpecialOpeningHoursPeriod;
